import React from "react";
import { Link, useLocation } from "wouter";
import {
  Home,
  Tag,
  Search,
  Heart,
  Upload,
  LayoutDashboard,
  CheckSquare,
  Download,
  Library,
  ChevronDown,
  Settings,
  Menu,
  LogOut,
  Activity,
  Copy,
  ShoppingCart,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface LayoutProps {
  children: React.ReactNode;
  variant?: "cookbook" | "admin";
}

const cookbookNav = [
  { href: "/", label: "Cookbook", mobileLabel: "Home", icon: Home },
  { href: "/categories", label: "Categories", mobileLabel: "Categories", icon: Tag },
  { href: "/favorites", label: "Favorites", mobileLabel: "Favorites", icon: Heart },
  { href: "/search", label: "Search", mobileLabel: "Search", icon: Search },
  { href: "/shopping-list", label: "Shopping", mobileLabel: "Shopping", icon: ShoppingCart },
];

const adminNav = [
  { href: "/admin/upload", label: "Import Recipes", icon: Upload },
  { href: "/admin/validate", label: "AI Validation", icon: CheckSquare },
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/duplicates", label: "Duplicates", icon: Copy },
  { href: "/admin/export", label: "Excel Export", icon: Download },
  { href: "/admin/recipes", label: "Manage Recipes", icon: Library },
  { href: "/admin/observability", label: "Observability", icon: Activity },
];

export function Layout({ children, variant }: LayoutProps) {
  const [location] = useLocation();
  const { logout } = useAuth();

  const isAdminPath = location.startsWith("/admin");
  const effectiveVariant = variant ?? (isAdminPath ? "admin" : "cookbook");
  const isAdmin = effectiveVariant === "admin";

  return (
    <div className="min-h-screen flex flex-col">
      <header data-print-hide className="sticky top-0 z-50 w-full glass-panel border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2.5 group">
              <img
                src="/logo.png"
                alt="Family Cookbook logo"
                className="h-8 w-8 object-contain"
              />
              <span
                className="text-xl font-bold text-foreground"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Family Cookbook
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {cookbookNav.map((item) => {
                const isActive =
                  item.href === "/"
                    ? location === "/"
                    : location.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200",
                      isAdminPath
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Settings className="w-4 h-4" />
                    Admin
                    <ChevronDown className="w-3 h-3 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Admin Tools
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {adminNav.map((item) => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        href={item.href}
                        className={cn(
                          "flex items-center gap-2 cursor-pointer",
                          location === item.href && "text-primary"
                        )}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>

      <main
        className={cn(
          "flex-1 py-8",
          isAdmin
            ? "max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8"
            : "w-full"
        )}
      >
        {children}
      </main>

      <nav data-print-hide className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/40">
        <div className="flex items-stretch h-16">
          {cookbookNav.map((item) => {
            const isActive =
              item.href === "/"
                ? location === "/"
                : location.startsWith(item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground"
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.mobileLabel}
              </Link>
            );
          })}

          <Sheet>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-1 text-xs font-medium transition-colors",
                  isAdminPath ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Menu className="w-5 h-5" />
                Menu
              </button>
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle
                  className="text-left"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Admin Tools
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {adminNav.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
                      location === item.href
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                ))}
                <div className="mt-2 pt-2 border-t border-border/60">
                  <button
                    onClick={logout}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all w-full"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <div data-print-hide className="md:hidden h-16 flex-shrink-0" aria-hidden="true" />
    </div>
  );
}
